// TimeTracker — the gs-task-tracker experience, lifted into a single
// component that takes `employee` as a prop and owns all timer/queue
// state. The outer App.jsx handles boot/picker/pin/session storage; the
// AppShell handles tab nav and global chrome (user identity, settings,
// switch). Everything else is a verbatim port of gs's App.jsx body.

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { formatDuration } from '../lib/format.js';
import { canInstall, isInstalled, onInstallAvailability, pipSupported, promptInstall } from '../lib/pwa.js';
import { playStart, playStop, playComplete } from '../lib/sound.js';
import { fireConfetti } from '../lib/confetti.js';
import TaskSearch from './TaskSearch.jsx';
import TaskView from './TaskView.jsx';
import StatsBar from './StatsBar.jsx';
import PipTimer from './PipTimer.jsx';
import TodayTasks from './TodayTasks.jsx';
import TaskQueuePanel from './TaskQueuePanel.jsx';
import ConflictModal from './ConflictModal.jsx';
import ConfirmModal from './ConfirmModal.jsx';
import JokeToast from './JokeToast.jsx';
import WelcomeCard from './WelcomeCard.jsx';
import { pickJoke } from '../lib/jokes-engine.js';
import { getSetting } from '../lib/settings.js';

// Bumped to v2 (gs precedent) so the first refresh after deploy shows
// the welcome card to eligible users regardless of any prior dismissal.
const WELCOME_SEEN_KEY = 'tt_welcome_seen_v2';

function welcomeAlreadyShownToday(uid, todayKey) {
  if (!uid || !todayKey) return false;
  try {
    const raw = localStorage.getItem(WELCOME_SEEN_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw);
    return map?.[uid] === todayKey;
  } catch { return false; }
}

function markWelcomeShown(uid, todayKey) {
  if (!uid || !todayKey) return;
  try {
    const raw = localStorage.getItem(WELCOME_SEEN_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[uid] = todayKey;
    localStorage.setItem(WELCOME_SEEN_KEY, JSON.stringify(map));
  } catch {}
}

export default function TimeTracker({ employee }) {
  const [activeTask, setActiveTask] = useState(null);
  const [now, setNow] = useState(Date.now());

  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  const [stats, setStats] = useState(null);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [pipOpen, setPipOpen] = useState(false);

  const [queue, setQueue] = useState([]);
  const [queueBusy, setQueueBusy] = useState({});
  const [queueError, setQueueError] = useState(null);
  const [conflictPrompt, setConflictPrompt] = useState(null);
  const [forkConfirmPrompt, setForkConfirmPrompt] = useState(null);
  const [autoStopPrompt, setAutoStopPrompt] = useState(null);
  // logIds we've already shown the auto-stop prompt for; prevents the
  // prompt re-firing on every /api/queue poll for the same closed log.
  // Persisted to localStorage so a page reload doesn't re-prompt for a
  // log the user already dismissed. Trimmed to the most-recent N to
  // keep the storage entry bounded.
  const AUTO_STOP_SEEN_KEY = 'tt_auto_stop_seen_v1';
  const AUTO_STOP_SEEN_MAX = 100;
  const seenAutoStopLogIds = useRef(loadAutoStopSeen());

  function loadAutoStopSeen() {
    try {
      const raw = localStorage.getItem(AUTO_STOP_SEEN_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  }

  function markAutoStopSeen(logId) {
    seenAutoStopLogIds.current.add(logId);
    try {
      const all = [...seenAutoStopLogIds.current];
      const trimmed = all.slice(-AUTO_STOP_SEEN_MAX);
      localStorage.setItem(AUTO_STOP_SEEN_KEY, JSON.stringify(trimmed));
    } catch {}
  }

  const [deletePrompt, setDeletePrompt] = useState(null);
  const [activeJoke, setActiveJoke] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  useEffect(() => {
    if (!employee?.welcomeEnabled) return;
    if (!getSetting('welcomeMode')) return;
    if (!stats?.today) return;
    if (welcomeAlreadyShownToday(employee.id, stats.today)) return;
    setWelcomeOpen(true);
  }, [employee, stats?.today]);

  const dismissWelcome = () => {
    if (employee && stats?.today) markWelcomeShown(employee.id, stats.today);
    setWelcomeOpen(false);
  };

  const maybeFireJoke = (item, opts = {}) => {
    if (!employee?.welcomeEnabled) return;
    if (!getSetting('welcomeMode')) return;
    const taskName = item?.taskName || opts.taskName || '';
    const elapsedSeconds = opts.elapsedSeconds ?? item?.elapsedSeconds;
    if (!Number.isFinite(elapsedSeconds)) return;
    const joke = pickJoke({ employee, taskName, elapsedSeconds });
    if (joke) setActiveJoke(joke);
  };

  useEffect(() => {
    const off = onInstallAvailability(setInstallAvailable);
    return off;
  }, []);

  const runningItem = queue.find(i => i.state === 'running') || null;

  // F4: auto-stop prompt — when the server-side sweep auto-closes a
  // timer that exceeded the duration cap, /api/queue returns the item
  // with autoClosed:true. Surface the first newly-discovered one as a
  // prompt asking the user whether to continue or complete. seenIds
  // ref ensures we don't keep re-prompting on every poll for an item
  // the user has already responded to (or dismissed).
  useEffect(() => {
    if (autoStopPrompt) return; // already showing one
    const newly = queue.find(i =>
      i.autoClosed &&
      i.state === 'paused' &&
      !seenAutoStopLogIds.current.has(i.logId)
    );
    if (!newly) return;
    seenAutoStopLogIds.current.add(newly.logId);
    setAutoStopPrompt(newly);
  }, [queue, autoStopPrompt]);

  useEffect(() => {
    if (!runningItem) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [runningItem?.logId]);

  // F6: auto-open PiP when a timer transitions from "nothing running"
  // to "something running". Skips re-opens for log-id changes during a
  // continuous session (so closing PiP manually doesn't immediately
  // reopen on the next /api/queue poll). Document Picture-in-Picture
  // requires a user gesture; starting a timer IS a gesture but the
  // token may not propagate through an async chain — if blocked, the
  // manual PiP button still works.
  const pipPrevLogId = useRef(null);
  useEffect(() => {
    if (!runningItem) {
      pipPrevLogId.current = null;
      return;
    }
    const prev = pipPrevLogId.current;
    pipPrevLogId.current = runningItem.logId;
    // Only auto-open on the FIRST appearance of a running item — not
    // every logId change (the "Continue" auto-stop flow creates a new
    // log on the same sub-task; we shouldn't slam the PiP open again
    // if the user closed it deliberately).
    if (prev === null && pipSupported() && !pipOpen) {
      try { setPipOpen(true); } catch {}
    }
  }, [runningItem?.logId]);

  // sendBeacon on tab-close: if a timer is running when the tab is
  // unloaded, fire-and-forget a pause-current to the worker so the
  // Time Log doesn't sit open until either the user returns or the 4h
  // sweep auto-closes it. sendBeacon is the only reliable way to do
  // network work during unload (fetch gets cancelled).
  //
  // Beacon can't set headers, so the active baseId is sent as a query
  // param (`?hlptBase=…`) and the backend's resolveBaseContext falls
  // back to that when X-HLPT-Base is absent.
  useEffect(() => {
    if (!employee?.id) return;
    const handleUnload = () => {
      if (!runningItem) return;
      try {
        const baseId = employee.baseId;
        if (!baseId) return;
        const payload = JSON.stringify({
          uid: employee.id,
          action: 'pause-current',
          reason: 'tab-closed',
        });
        const blob = new Blob([payload], { type: 'application/json' });
        const url = `/api/queue-action?hlptBase=${encodeURIComponent(baseId)}`;
        navigator.sendBeacon?.(url, blob);
      } catch {}
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [employee?.id, employee?.baseId, runningItem?.logId]);

  // Debounce stats refreshes to at most one call every 8s.
  const statsTimer = useRef(null);
  const lastStatsAt = useRef(0);
  useEffect(() => {
    if (!employee?.id) return;
    let cancelled = false;
    const fire = () => {
      lastStatsAt.current = Date.now();
      api.myStats(employee.id, 7, { ownerName: employee.name })
        .then(d => { if (!cancelled) setStats(d); })
        .catch(() => {});
    };
    const elapsed = Date.now() - lastStatsAt.current;
    if (elapsed >= 8000 || !stats) {
      fire();
    } else {
      if (statsTimer.current) clearTimeout(statsTimer.current);
      statsTimer.current = setTimeout(fire, 8000 - elapsed);
    }
    return () => {
      cancelled = true;
      if (statsTimer.current) { clearTimeout(statsTimer.current); statsTimer.current = null; }
    };
  }, [employee?.id, statsRefreshKey]);

  const loadQueue = async () => {
    try {
      const d = await api.queue(employee.id);
      setQueue(d.items || []);
      setQueueError(null);
    } catch (e) {
      setQueueError(e.message);
    }
  };

  // Initial queue load on mount.
  useEffect(() => {
    if (!employee?.id) return;
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  // Poll the queue while the tab is visible (audit M1 — propagates actions
  // taken in another tab / on another device / via the Airtable button-script).
  useEffect(() => {
    if (!employee?.id) return;
    let stopped = false;
    let timerId;
    const tick = async () => {
      if (stopped) return;
      if (document.visibilityState === 'visible') {
        try {
          const d = await api.queue(employee.id);
          if (!stopped) {
            setQueue(d.items || []);
            setQueueError(null);
          }
        } catch {}
      }
      if (!stopped) timerId = setTimeout(tick, 30_000);
    };
    timerId = setTimeout(tick, 30_000);
    const onVis = () => {
      if (document.visibilityState === 'visible' && !stopped) {
        if (timerId) clearTimeout(timerId);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      if (timerId) clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [employee?.id]);

  const setBusy = (subTaskId, action) =>
    setQueueBusy(prev => action ? { ...prev, [subTaskId]: action } : (() => { const n = { ...prev }; delete n[subTaskId]; return n; })());

  const callQueueAction = async (payload) =>
    api.queueAction({ uid: employee.id, ...payload });

  const refreshActiveTask = async () => {
    if (!activeTask?.id) return;
    try {
      const d = await api.tasksByIds([activeTask.id]);
      const t = (d.tasks || [])[0];
      if (t) setActiveTask(t);
    } catch {}
  };

  const handleCompleteTask = async (task) => {
    setBusy(`task:${task.id}`, 'complete');
    try {
      await api.completeTask(employee.id, task.id);
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      if (activeTask?.id === task.id) await refreshActiveTask();
      playComplete();
      fireConfetti();
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(`task:${task.id}`, null);
    }
  };

  const handleDeleteTask = async (task) => {
    setBusy(`task:${task.id}`, 'delete');
    try {
      await api.deleteTask(employee.id, task.id);
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      if (activeTask?.id === task.id) setActiveTask(null);
      setDeletePrompt(null);
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(`task:${task.id}`, null);
    }
  };

  const handleRestartFromTodayTask = async (todayTask) => {
    if (!todayTask?.taskId) return;
    setBusy(`task:${todayTask.taskId}`, 'restart');
    try {
      const d = await api.tasksByIds([todayTask.taskId]);
      const sourceTask = (d.tasks || [])[0];
      const sub = sourceTask?.subTaskIds?.[0];
      if (!sub) return;
      await handleStart(sub);
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(`task:${todayTask.taskId}`, null);
    }
  };

  const handleMoveQueueItem = async (subTaskId, direction) => {
    const order = queue.map(q => q.subTaskId);
    const idx = order.indexOf(subTaskId);
    if (idx < 0) return;
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= order.length) return;
    const newOrder = [...order];
    [newOrder[idx], newOrder[swapWith]] = [newOrder[swapWith], newOrder[idx]];
    const optimistic = newOrder.map(id => queue.find(q => q.subTaskId === id)).filter(Boolean);
    setQueue(optimistic);
    try {
      await api.reorderQueue(employee.id, newOrder);
    } catch (e) {
      setQueueError(e.message);
      await loadQueue();
    }
  };

  const handleStart = async (subTaskId, conflictAction, confirmFork) => {
    setBusy(subTaskId, 'start');
    try {
      const res = await callQueueAction({ action: 'start', subTaskId, conflictAction, confirmFork });
      if (res.cancelled) return;
      if (res.forkedTaskId) {
        try {
          const d = await api.tasksByIds([res.forkedTaskId]);
          const t = (d.tasks || [])[0];
          if (t) setActiveTask(t);
        } catch {}
      }
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      await refreshActiveTask();
      playStart();
    } catch (e) {
      // Two CONFLICT variants — order matters: needsConfirm first since
      // both set e.code='CONFLICT' but only needsConfirm carries the
      // reopen-completed-task semantics.
      if (e.code === 'CONFLICT' && e.needsConfirm) {
        setForkConfirmPrompt({
          subTaskId,
          conflictAction,
          message: e.message,
          parentTaskName: e.parentTaskName,
        });
      } else if (e.code === 'CONFLICT') {
        setConflictPrompt({ running: e.running, intent: { type: 'start', subTaskId } });
      } else {
        setQueueError(e.message);
      }
    } finally {
      setBusy(subTaskId, null);
    }
  };

  const handlePause = async (item) => {
    setBusy(item.subTaskId, 'pause');
    try {
      await callQueueAction({ action: 'pause', logId: item.logId, subTaskId: item.subTaskId, status: 'On Hold' });
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      await refreshActiveTask();
      playStop();
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(item.subTaskId, null);
    }
  };

  const handleResume = async (item, conflictAction) => {
    setBusy(item.subTaskId, 'resume');
    try {
      const res = await callQueueAction({ action: 'resume', logId: item.logId, subTaskId: item.subTaskId, conflictAction });
      if (res.cancelled) return;
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      await refreshActiveTask();
      playStart();
    } catch (e) {
      if (e.code === 'CONFLICT') {
        setConflictPrompt({ running: e.running, intent: { type: 'resume', item } });
      } else {
        setQueueError(e.message);
      }
    } finally {
      setBusy(item.subTaskId, null);
    }
  };

  const handleComplete = async (item) => {
    setBusy(item.subTaskId, 'complete');
    const liveElapsed = item.state === 'running' && item.startTime
      ? Math.max(0, Math.floor((Date.now() - new Date(item.startTime).getTime()) / 1000))
      : item.elapsedSeconds;
    try {
      await callQueueAction({ action: 'complete', logId: item.logId, subTaskId: item.subTaskId });
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      await refreshActiveTask();
      playComplete();
      fireConfetti();
      maybeFireJoke(item, { elapsedSeconds: liveElapsed });
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(item.subTaskId, null);
    }
  };

  const handleNext = async (item) => {
    if (!item.nextSubTask?.id) return;
    setBusy(item.subTaskId, 'next');
    const liveElapsed = item.state === 'running' && item.startTime
      ? Math.max(0, Math.floor((Date.now() - new Date(item.startTime).getTime()) / 1000))
      : item.elapsedSeconds;
    try {
      await callQueueAction({
        action: 'next',
        logId: item.logId,
        subTaskId: item.subTaskId,
        nextSubTaskId: item.nextSubTask.id,
        completionStatus: 'Complete',
      });
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      await refreshActiveTask();
      playComplete();
      maybeFireJoke(item, { elapsedSeconds: liveElapsed });
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(item.subTaskId, null);
    }
  };

  const handleSetStatus = async (item, status) => {
    setBusy(item.subTaskId, 'set-status');
    try {
      await callQueueAction({ action: 'set-status', logId: item.logId, subTaskId: item.subTaskId, status });
      await loadQueue();
      setStatsRefreshKey(k => k + 1);
      await refreshActiveTask();
      if (status === 'Complete') { playComplete(); fireConfetti(); } else { playStop(); }
    } catch (e) {
      setQueueError(e.message);
    } finally {
      setBusy(item.subTaskId, null);
    }
  };

  const handleConflictChoice = async (action) => {
    if (!conflictPrompt) return;
    const { intent } = conflictPrompt;
    setConflictPrompt(null);
    if (intent.type === 'start') {
      await handleStart(intent.subTaskId, action);
    } else if (intent.type === 'resume') {
      await handleResume(intent.item, action);
    }
  };

  const handleQueueOpen = async (item) => {
    if (!item.taskId) return;
    try {
      const d = await api.tasksByIds([item.taskId]);
      const t = (d.tasks || [])[0];
      if (t) setActiveTask(t);
    } catch {}
  };

  // Bring the running sub-task's parent task into TaskView, close PiP,
  // and scroll the main column to the top. Called when the user clicks
  // "Back to Co-Pilot" from the PiP, and on initial queue load if a
  // timer is already running — so the user lands on the active work,
  // not on the empty "today's tasks" panel.
  const focusRunningTask = async () => {
    if (!runningItem?.taskId) return;
    setPipOpen(false);
    try {
      const d = await api.tasksByIds([runningItem.taskId]);
      const t = (d.tasks || [])[0];
      if (t) setActiveTask(t);
    } catch {}
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Auto-focus the running task once, the first time the queue load
  // surfaces one. Uses a ref to avoid re-focusing on every poll.
  const autoFocusedFor = useRef(null);
  useEffect(() => {
    if (!runningItem?.taskId) return;
    if (activeTask) return;
    if (autoFocusedFor.current === runningItem.taskId) return;
    autoFocusedFor.current = runningItem.taskId;
    focusRunningTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningItem?.taskId, activeTask]);

  const liveSeconds = runningItem ? Math.floor((now - new Date(runningItem.startTime).getTime()) / 1000) : 0;
  const runningStartedToday = runningItem?.startTime && stats?.today && stats?.tzOffsetMin != null
    ? (() => {
        const tzMin = stats.tzOffsetMin;
        const startMs = new Date(runningItem.startTime).getTime();
        const localStart = new Date(startMs - tzMin * 60000);
        const y = localStart.getUTCFullYear();
        const m = String(localStart.getUTCMonth() + 1).padStart(2, '0');
        const d = String(localStart.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}` === stats.today;
      })()
    : false;
  const liveSecondsForToday = runningStartedToday ? liveSeconds : 0;
  const longRunningHours = runningItem ? Math.floor(liveSeconds / 3600) : 0;
  const runningOnOwnedTask = Boolean(
    runningItem?.subTaskId && stats?.todayTasks &&
    runningItem.taskId && stats.todayTasks.some(t => t.taskId === runningItem.taskId)
  );
  const ownedTodayTotal = (stats?.owned?.todaySeconds || 0) + (runningOnOwnedTask && runningStartedToday ? liveSeconds : 0);
  const installed = isInstalled();

  const runningLogForPip = runningItem ? {
    logId: runningItem.logId,
    subTaskId: runningItem.subTaskId,
    startTime: runningItem.startTime,
    subTaskName: runningItem.subTaskName,
    taskId: runningItem.taskId,
    taskName: runningItem.taskName,
  } : null;

  return (
    <>
      {/* Tracker sub-header: running chip + PiP + install
          The chip is dual-purpose so a glance at the top of the page
          always reflects "the thing the user most wants to see":
            - while a timer is running, the LIVE elapsed seconds. Ticks
              every second from the parent `now` state — no stale lag
              waiting for stats to refresh.
            - when no timer is running, the daily total for owned tasks
              so the user can see how much they've logged today. */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <div
          title={runningItem ? `Live timer · today's owned total: ${formatDuration(ownedTodayTotal)}` : `Today's total on your tasks`}
          className={`flex items-center gap-2 px-2.5 py-1 rounded-md border ${runningItem ? 'bg-running/10 border-running/30' : 'bg-panel border-border'}`}
        >
          <div className={`w-2 h-2 rounded-full ${runningItem ? 'bg-running pulse-dot' : 'bg-muted'}`} />
          <span className={`text-[10px] uppercase tracking-wider ${runningItem ? 'text-running/80' : 'text-muted'}`}>
            {runningItem ? 'Running' : 'Today'}
          </span>
          <span className={`font-mono text-xs font-semibold ${runningItem ? 'text-running' : 'text-text/80'}`}>
            {runningItem ? formatDuration(liveSeconds) : formatDuration(ownedTodayTotal)}
          </span>
        </div>
        {/* PiP button — prominent when a timer is running so users
            actually notice it. Label + icon, accent-colored. */}
        {runningItem && pipSupported() && !pipOpen && (
          <button
            onClick={() => setPipOpen(true)}
            title="Open the timer in a floating window so you can see it while using other tabs."
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent hover:bg-accent2 text-white text-xs font-semibold transition-colors"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <rect x="12" y="11" width="8" height="6" rx="1" fill="currentColor" />
            </svg>
            Picture-in-Picture
          </button>
        )}
        {installAvailable && !installed && (
          <button
            onClick={() => promptInstall()}
            className="text-xs px-2.5 py-1 rounded-md border border-accent/40 bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
          >
            Install
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <StatsBar
            stats={stats}
            runningExtraSeconds={liveSecondsForToday}
            runningOnOwnedTask={runningOnOwnedTask}
            onTaskOpen={async taskId => {
              try {
                const d = await api.tasksByIds([taskId]);
                const t = (d.tasks || [])[0];
                if (t) setActiveTask(t);
              } catch {}
            }}
          />

          {runningItem && !runningStartedToday && (
            <div className="p-3 rounded-lg border border-warn/40 bg-warn/10 text-sm text-warn flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold">⚠ Long-running timer from a previous day</div>
                <div className="text-xs text-warn/80 mt-0.5">
                  "{runningItem.subTaskName || 'A task'}" has been running for {longRunningHours}h. Probably forgotten — stop it to clean up.
                </div>
              </div>
              <button
                onClick={() => handlePause(runningItem)}
                className="px-3 py-1.5 rounded-md bg-warn/20 hover:bg-warn/30 border border-warn/40 text-warn text-xs font-semibold transition-colors flex-shrink-0"
              >
                Stop now
              </button>
            </div>
          )}

          {activeTask ? (
            <TaskView
              task={activeTask}
              employee={employee}
              runningItem={runningItem}
              queue={queue}
              queueBusy={queueBusy}
              now={now}
              onStart={handleStart}
              onPause={handlePause}
              onResume={handleResume}
              onComplete={handleComplete}
              onBack={() => setActiveTask(null)}
              onDelete={(t) => setDeletePrompt(t)}
            />
          ) : (
            <TodayTasks
              stats={stats}
              runningLog={runningLogForPip}
              runningExtraSeconds={liveSeconds}
              onSelect={t => setActiveTask(t)}
              onRestart={handleRestartFromTodayTask}
              restartingTaskIds={Object.fromEntries(
                Object.entries(queueBusy)
                  .filter(([k, v]) => k.startsWith('task:') && v === 'restart')
                  .map(([k]) => [k.slice(5), true])
              )}
            />
          )}
        </div>

        <aside className="lg:col-span-1 space-y-4">
          {!activeTask && (
            <TaskSearch
              uid={employee.id}
              name={employee.name}
              queue={queue}
              onSelect={t => setActiveTask(t)}
              onCompleteTask={handleCompleteTask}
              completingTaskIds={Object.fromEntries(
                Object.entries(queueBusy)
                  .filter(([k, v]) => k.startsWith('task:') && v === 'complete')
                  .map(([k]) => [k.slice(5), 'complete'])
              )}
              taskFormUrl={employee.taskFormUrl}
            />
          )}
          <TaskQueuePanel
            items={queue}
            busyMap={queueBusy}
            error={queueError}
            now={now}
            onResume={handleResume}
            onPause={handlePause}
            onComplete={handleComplete}
            onSetStatus={handleSetStatus}
            onOpen={handleQueueOpen}
            onNext={handleNext}
            onMoveUp={(item) => handleMoveQueueItem(item.subTaskId, 'up')}
            onMoveDown={(item) => handleMoveQueueItem(item.subTaskId, 'down')}
          />
        </aside>
      </div>

      {pipOpen && (
        <PipTimer
          runningLog={runningLogForPip}
          runningItem={runningItem}
          now={now}
          onPause={() => runningItem && handlePause(runningItem)}
          onComplete={() => runningItem && handleComplete(runningItem)}
          onNext={() => runningItem && handleNext(runningItem)}
          onClose={() => setPipOpen(false)}
          onReturnToTab={focusRunningTask}
        />
      )}

      {conflictPrompt && (
        <ConflictModal
          running={conflictPrompt.running}
          newTaskName={conflictPrompt.intent?.type === 'resume' ? conflictPrompt.intent.item.subTaskName : null}
          onChoose={handleConflictChoice}
          onCancel={() => setConflictPrompt(null)}
        />
      )}

      {deletePrompt && (
        <ConfirmModal
          title={`Delete "${deletePrompt.name}"?`}
          message={`This permanently deletes the task, all its sub-tasks, and every Time Log on those sub-tasks from Airtable. This cannot be undone. Are you sure?`}
          confirmLabel="Delete forever"
          danger
          onConfirm={() => handleDeleteTask(deletePrompt)}
          onCancel={() => setDeletePrompt(null)}
        />
      )}

      {forkConfirmPrompt && (
        <ConfirmModal
          title={forkConfirmPrompt.parentTaskName
            ? `Restart "${forkConfirmPrompt.parentTaskName}"?`
            : 'Restart completed task?'}
          message={forkConfirmPrompt.message || 'This task was already marked Complete. Start a new copy and move it back to In Progress?'}
          confirmLabel="Restart task"
          cancelLabel="Cancel"
          danger={false}
          onConfirm={() => {
            const p = forkConfirmPrompt;
            setForkConfirmPrompt(null);
            // Retry with confirmFork:true — server will now run the
            // auto-fork logic (or just reopen the existing sub-task)
            // without the gate firing.
            return handleStart(p.subTaskId, p.conflictAction, true);
          }}
          onCancel={() => setForkConfirmPrompt(null)}
        />
      )}

      {autoStopPrompt && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => {
            // Click-outside acts as Keep-on-hold: the server already
            // moved the sub-task to On Hold during auto-close, so we
            // just persist the dismissal so the prompt doesn't re-fire
            // on the next /api/queue poll or page reload.
            const p = autoStopPrompt;
            setAutoStopPrompt(null);
            markAutoStopSeen(p.logId);
          }}
        >
          <div
            className="w-full max-w-md bg-panel border border-warn/50 rounded-2xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="text-sm font-semibold text-warn mb-2 flex items-center gap-2">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                Timer auto-stopped at the 4-hour cap
              </div>
              <div className="text-sm text-text/80 leading-relaxed">
                Your timer on{' '}
                <span className="font-medium">
                  "{autoStopPrompt.subTaskName || 'this sub-task'}"
                </span>
                {autoStopPrompt.taskName && (
                  <> (in <span className="font-medium">{autoStopPrompt.taskName}</span>)</>
                )}
                {' '}reached the 4-hour limit and was auto-stopped. The recorded
                time is capped at 4 hours; the sub-task has been moved to{' '}
                <span className="font-medium">On Hold</span>. What now?
              </div>
            </div>
            <div className="p-4 border-t border-border bg-panel2/50 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
              <button
                onClick={() => {
                  // Keep on hold: server already set Status='On Hold'
                  // and cleared Active Time Log during auto-close, so
                  // this is a confirmation. No additional API call.
                  const p = autoStopPrompt;
                  setAutoStopPrompt(null);
                  markAutoStopSeen(p.logId);
                }}
                className="px-4 py-2 text-sm rounded-md border border-border hover:bg-panel2 text-text transition-colors"
              >
                Keep on hold
              </button>
              <button
                onClick={() => {
                  const p = autoStopPrompt;
                  setAutoStopPrompt(null);
                  markAutoStopSeen(p.logId);
                  // Mark Complete: flips the sub-task to Complete and
                  // removes it from the queue.
                  return handleComplete(p);
                }}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-running hover:bg-running/90 text-white transition-colors"
              >
                Mark complete
              </button>
              <button
                onClick={() => {
                  const p = autoStopPrompt;
                  setAutoStopPrompt(null);
                  markAutoStopSeen(p.logId);
                  // Continue: start a fresh timer on the same sub-task.
                  // confirmFork:true bypasses the parent-Complete gate
                  // in case the parent task got marked Complete as a
                  // side-effect (e.g. sole sub-task was Complete'd).
                  return handleStart(p.subTaskId, undefined, true);
                }}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-accent hover:bg-accent2 text-white transition-colors"
              >
                Continue working
              </button>
            </div>
          </div>
        </div>
      )}

      {activeJoke && (
        <JokeToast joke={activeJoke} onDismiss={() => setActiveJoke(null)} />
      )}

      {welcomeOpen && (
        <WelcomeCard
          employee={employee}
          stats={stats}
          onDismiss={dismissWelcome}
        />
      )}
    </>
  );
}
